import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ConfigService } from './config.service';
import { WsTicketService } from './ws-ticket.service';

export interface FileChangeEvent {
  type: 'file-change';
  path: string;
  eventType: string;
  timestamp: number;
}

/**
 * Service for subscribing to file change notifications via WebSocket to mux.
 * Uses per-user SSH to monitor files, enabling multi-tenant file watching.
 *
 * This enables real-time updates to Monaco editors when files are modified
 * externally (e.g., by an AI agent).
 */
@Injectable({
  providedIn: 'root'
})
export class FileWatchService implements OnDestroy {
  private fileChanges$ = new Subject<FileChangeEvent>();
  private watchedPaths = new Set<string>();
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3;
  private reconnectDelay = 2000;
  private pendingWatches: string[] = [];
  private disabled = false;  // Set to true if WebSocket is unsupported (e.g., Cloudflare blocking)

  constructor(private _wsTickets: WsTicketService) {}

  ngOnDestroy(): void {
    this.disconnect();
  }

  /**
   * Connect to the mux file-watch WebSocket
   */
  private async connect(): Promise<void> {
    if (this.disabled) {
      return;  // WebSocket not supported in this environment
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      // Single-use ticket for the handshake (no bearer in the URL)
      const ticket = await this._wsTickets.ticket();
      if (!ticket) {
        console.error('[FileWatch] No WebSocket ticket available');
        return;
      }

      // Build WebSocket URL with the ticket
      const muxUrl = ConfigService.muxUrl;
      let wsUrl: string;

      if (muxUrl.startsWith('http://') || muxUrl.startsWith('https://')) {
        // Absolute URL - convert protocol
        const wsProtocol = muxUrl.startsWith('https') ? 'wss' : 'ws';
        const wsHost = muxUrl.replace(/^https?:\/\//, '');
        wsUrl = `${wsProtocol}://${wsHost}/ws/file-watch?ticket=${encodeURIComponent(ticket)}`;
      } else {
        // Relative URL - use current page's protocol and host
        const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
        const wsPath = muxUrl.startsWith('/') ? muxUrl : `/${muxUrl}`;
        wsUrl = `${wsProtocol}://${window.location.host}${wsPath}/ws/file-watch?ticket=${encodeURIComponent(ticket)}`;
      }

      console.log('[FileWatch] Connecting to WebSocket:', wsUrl.split('?')[0]);
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('[FileWatch] WebSocket connected');
        this.reconnectAttempts = 0;

        // Send any pending watch requests
        for (const path of this.pendingWatches) {
          this.sendWatchCommand(path);
        }
        this.pendingWatches = [];

        // Re-watch all previously watched files
        for (const path of this.watchedPaths) {
          this.sendWatchCommand(path);
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('[FileWatch] Message received:', data);
          if (data.type === 'file-change') {
            this.fileChanges$.next(data as FileChangeEvent);
          }
        } catch (e) {
          console.error('[FileWatch] Failed to parse message:', e);
        }
      };

      this.ws.onerror = (err) => {
        console.error('[FileWatch] WebSocket error:', err);
      };

      this.ws.onclose = (event) => {
        this.ws = null;

        // Code 1006 typically means connection failed (e.g., Cloudflare blocking WebSocket)
        if (event.code === 1006 && this.reconnectAttempts >= this.maxReconnectAttempts) {
          // Disable file watch - likely not supported in this environment
          console.warn('[FileWatch] WebSocket not available - file watching disabled');
          this.disabled = true;
          this.pendingWatches = [];
          return;
        }

        // Attempt reconnect if not intentional close and files are being watched
        if (this.watchedPaths.size > 0 && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          setTimeout(() => this.connect(), this.reconnectDelay * this.reconnectAttempts);
        }
      };
    } catch (e) {
      // Connection failed - will be handled by onclose
    }
  }

  /**
   * Disconnect from WebSocket
   */
  private disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.watchedPaths.clear();
    this.pendingWatches = [];
  }

  /**
   * Send a watch command to the server
   */
  private sendWatchCommand(filePath: string): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ command: 'watch', path: filePath }));
    }
  }

  /**
   * Send an unwatch command to the server
   */
  private sendUnwatchCommand(filePath: string): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ command: 'unwatch', path: filePath }));
    }
  }

  /**
   * Start watching a file for changes
   */
  watchFile(filePath: string): void {
    console.log('[FileWatch] watchFile called:', filePath, 'disabled:', this.disabled, 'already watching:', this.watchedPaths.has(filePath));
    if (this.disabled || this.watchedPaths.has(filePath)) {
      return;
    }

    this.watchedPaths.add(filePath);

    // Connect if not connected
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.pendingWatches.push(filePath);
      this.connect();
    } else {
      this.sendWatchCommand(filePath);
    }
  }

  /**
   * Stop watching a file
   */
  unwatchFile(filePath: string): void {
    if (!this.watchedPaths.has(filePath)) {
      return;
    }

    this.watchedPaths.delete(filePath);
    this.sendUnwatchCommand(filePath);

    // Disconnect if no more watches
    if (this.watchedPaths.size === 0) {
      this.disconnect();
    }
  }

  /**
   * Get an observable of changes for a specific file
   */
  getChanges(filePath: string): Observable<FileChangeEvent> {
    return this.fileChanges$.pipe(
      filter(event => event.path === filePath)
    );
  }

  /**
   * Get an observable of all file changes
   */
  getAllChanges(): Observable<FileChangeEvent> {
    return this.fileChanges$.asObservable();
  }

  /**
   * Check if a file is being watched
   */
  isWatching(filePath: string): boolean {
    return this.watchedPaths.has(filePath);
  }
}
